import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BedBadge, FreshnessBadge } from "@/components/FacilityCard";
import { InquiryDialog } from "@/components/InquiryDialog";
import { AuthDialog } from "@/components/AuthDialog";
import { ClaimDialog } from "@/components/ClaimDialog";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { categoryLabel } from "@/lib/categories";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { toast } from "sonner";
import {
  Heart, MapPin, Phone, BedDouble, BadgeCheck, X, MessageSquare, Building2, ShieldCheck, Globe, ExternalLink,
} from "lucide-react";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

export function FacilityDetailPanel({ facility, open, onOpenChange, isFavorite, onToggleFavorite }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  useEffect(() => {
    if (open && facility?.facility_id) {
      track("facility_view", { facility_id: facility.facility_id });
    }
  }, [open, facility?.facility_id]);

  if (!facility) return null;
  const images = facility.photos?.length ? facility.photos : [FALLBACKS[(facility.licensed_beds_total || 0) % 2]];

  const handleFav = () => {
    if (!user) { setAuthOpen(true); return; }
    onToggleFavorite(facility);
  };
  const handleInquiry = () => {
    track("inquiry_click", { facility_id: facility.facility_id });
    if (!user) { setAuthOpen(true); return; }
    setInquiryOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-y-auto" data-testid="facility-detail">
        <button onClick={() => onOpenChange(false)} aria-label="Close facility details" className="absolute top-4 right-4 z-20 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center" data-testid="close-detail">
          <X className="h-5 w-5" />
        </button>
        <div className="relative h-64">
          <img src={images[0]} alt={facility.name} className="w-full h-full object-cover" />
          <div className="absolute bottom-4 left-4">{facility.claimed && (
            <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-white/90 text-[#1A365D]"><BadgeCheck className="h-4 w-4" /> Verified listing</span>
          )}</div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-[#5C6660] font-medium mb-1">{categoryLabel(settings, facility.category, facility.facility_type)}{facility.facility_type_detail ? ` · ${facility.facility_type_detail}` : ""}</p>
            <h2 className="font-heading text-3xl font-medium text-[#1C211F] leading-tight">{facility.name}</h2>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {facility.tier === "premium" && (
              <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#0077B6] text-white" data-testid="detail-featured">Featured</span>
            )}
            <BedBadge beds={facility.available_beds} tier={facility.tier} />
            <span className="text-sm text-[#5C6660]">·</span>
            <span className="text-sm text-[#4A534E] font-mono-data">{facility.licensed_beds_total} licensed beds</span>
          </div>
          <div className="-mt-2"><FreshnessBadge facility={facility} /></div>

          <div className="flex gap-3">
            {facility.inquiries_on ? (
              <Button onClick={handleInquiry} data-testid="contact-inquiry-btn" className="flex-1 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12">
                <MessageSquare className="h-4 w-4 mr-2" /> Contact facility
              </Button>
            ) : (
              <a href={`tel:${facility.telephone}`} onClick={() => track("phone_click", { facility_id: facility.facility_id })} className="flex-1" data-testid="call-facility-btn">
                <Button className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12">
                  <Phone className="h-4 w-4 mr-2" /> Call to inquire
                </Button>
              </a>
            )}
            <Button onClick={handleFav} variant="outline" data-testid="detail-favorite-btn" aria-label={isFavorite ? "Remove from saved facilities" : "Save this facility"} aria-pressed={isFavorite} className="rounded-xl h-12 w-12 border-[#E8E6DF] p-0">
              <Heart className={`h-5 w-5 ${isFavorite ? "fill-[#0077B6] text-[#0077B6]" : "text-[#4A534E]"}`} />
            </Button>
          </div>

          <div className="space-y-3 border-t border-[#E8E6DF] pt-5">
            <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#5C6660] mt-0.5" /><div><div className="text-sm text-[#1C211F]">{facility.address_full}</div><div className="text-xs text-[#5C6660]">{facility.county} County</div></div></div>
            <a href={`tel:${facility.telephone}`} onClick={() => track("phone_click", { facility_id: facility.facility_id })} className="flex items-center gap-3 text-[#1C211F] hover:text-[#1A365D]"><Phone className="h-5 w-5 text-[#5C6660]" /><span className="text-sm">{facility.telephone}</span></a>
            <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#5C6660]" /><span className="text-sm text-[#1C211F]">CDPHE {facility.cdphe_licensed} · {facility.operating_status}</span></div>
            {facility.tier === "premium" && facility.website && (
              <a href={facility.website.startsWith("http") ? facility.website : `https://${facility.website}`} target="_blank" rel="noopener noreferrer" data-testid="facility-website-link" className="flex items-center gap-3 text-[#0077B6] hover:underline">
                <Globe className="h-5 w-5 text-[#0077B6]" /><span className="text-sm font-medium">Visit website</span>
              </a>
            )}
            <Link to={`/facility/${facility.facility_id}`} data-testid="view-full-page-link" className="flex items-center gap-3 text-[#0077B6] hover:underline">
              <ExternalLink className="h-5 w-5 text-[#0077B6]" /><span className="text-sm font-medium">View full page</span>
            </Link>
          </div>

          {facility.description && (
            <div className="border-t border-[#E8E6DF] pt-5">
              <h3 className="font-heading text-lg font-medium mb-2">About</h3>
              <p className="text-[#4A534E] leading-relaxed text-sm">{facility.description}</p>
            </div>
          )}

          {facility.amenities?.length > 0 && (
            <div className="border-t border-[#E8E6DF] pt-5">
              <h3 className="font-heading text-lg font-medium mb-3">Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {facility.amenities.map((a) => (
                  <span key={a} className="px-3 py-1.5 rounded-full bg-[#EFEEE8] text-sm text-[#2A312D]">{a}</span>
                ))}
              </div>
            </div>
          )}

          {facility.payment_types?.length > 0 && (
            <div className="border-t border-[#E8E6DF] pt-5" data-testid="detail-payment-types">
              <h3 className="font-heading text-lg font-medium mb-3">Payment types accepted</h3>
              <div className="flex flex-wrap gap-2">
                {facility.payment_types.map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-full bg-[#E3F3ED] text-sm text-[#1A365D] font-medium">{p}</span>
                ))}
              </div>
            </div>
          )}

          {!facility.claimed && (
            <div className="border-t border-[#E8E6DF] pt-5">
              <div className="bg-[#E3F3ED] rounded-2xl p-5">
                <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-1"><Building2 className="h-5 w-5" /> Is this your facility?</h3>
                <p className="text-sm text-[#4A534E] mb-3">Claim this listing to manage your profile, add photos and amenities, and keep bed availability current. We'll verify your request before approving.</p>
                <Button onClick={() => setClaimOpen(true)} data-testid="claim-btn" className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl">
                  Claim this listing
                </Button>
              </div>
            </div>
          )}
        </div>

        <InquiryDialog facility={facility} open={inquiryOpen} onOpenChange={setInquiryOpen} user={user} />
        <ClaimDialog facility={facility} open={claimOpen} onOpenChange={setClaimOpen} user={user} onSubmitted={() => onOpenChange(false)} />
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      </SheetContent>
    </Sheet>
  );
}
